/**
 * What the exported review says — Step 54.
 *
 * Kai: *"all the elements that make up that pdf need to be able to be
 * customised & fine tuned"*, *"they might not be accurate"*, *"all the dates,
 * times, bits of text etc need to be changeable"*.
 *
 * They might well not be accurate. Every value here is derived from
 * timestamps recorded for operational reasons — `resolvedAt` moves whenever
 * somebody reopens and re-resolves a ticket — and his own export showed a
 * sixty-hour mean-time-to-resolve on an outage that lasted seven seconds,
 * because the ticket had been resolved that morning and declared a major
 * incident that evening.
 *
 * ── Why it reads as a LIST, not a form ─────────────────────────────────
 *
 * The first cut put a label, the derived value, a text box and two icon
 * buttons on every one of eighteen rows. Kai: *"it's very cluttered"*, and it
 * was — ninety controls for a job that is usually "fix one number".
 *
 * So the default state is a read-only list of what the document will say, and
 * a row becomes editable when you click it. Overridden rows are marked, and
 * only those carry the undo. The common case is now reading; editing is the
 * exception, which is the right way round.
 */
import { useState } from 'react';
import { Eye, EyeOff, Pencil, Undo2 } from 'lucide-react';
import { PIR_BLOCKS, PIR_FACTS, isHidden, isOverridden, type PirDocConfig } from '@itsm/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

export function DocumentEditor({
  config,
  onChange,
  derived,
  readOnly,
}: {
  config: PirDocConfig;
  onChange: (next: PirDocConfig) => void;
  derived: Record<string, string>;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);

  const setOverride = (key: string, value: string | undefined) => {
    const overrides = { ...(config.overrides ?? {}) };
    // `undefined` REMOVES the override (back to derived); `''` keeps it as a
    // deliberate blank. Two different intentions, and the delete is what makes
    // the first one reachable.
    if (value === undefined) delete overrides[key];
    else overrides[key] = value;
    onChange({ ...config, overrides });
  };
  const toggleHidden = (key: string) => {
    const hidden = new Set(config.hidden ?? []);
    if (hidden.has(key)) hidden.delete(key);
    else hidden.add(key);
    onChange({ ...config, hidden: [...hidden] });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        What the PDF will say. Click a line to change the wording; the eye leaves it out.
      </p>

      <div className="flex flex-wrap gap-1.5">
        {PIR_BLOCKS.map((b) => {
          const off = isHidden(config, b.key);
          return (
            <button
              key={b.key}
              type="button"
              disabled={readOnly}
              onClick={() => toggleHidden(b.key)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                off ? 'border-dashed text-muted-foreground line-through' : 'bg-accent/50 hover:bg-accent',
              )}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {(['details', 'response'] as const).map((block) => {
        if (isHidden(config, block)) return null;
        return (
          <div key={block} className="overflow-hidden rounded-lg border">
            <p className="border-b bg-muted/40 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {PIR_BLOCKS.find((b) => b.key === block)?.label}
            </p>
            <div className="divide-y">
              {PIR_FACTS.filter((f) => f.block === block).map((f) => {
                const off = isHidden(config, f.key);
                const edited = isOverridden(config, f.key);
                const shown = edited ? (config.overrides?.[f.key] ?? '') : (derived[f.key] ?? '—');
                const open = editing === f.key;
                return (
                  <div key={f.key} className="group flex items-center gap-2 px-3 py-1.5 text-sm">
                    <span className={cn('w-48 shrink-0 text-xs', off && 'text-muted-foreground')}>
                      {f.label}
                    </span>

                    {open ? (
                      <Input
                        autoFocus
                        value={config.overrides?.[f.key] ?? derived[f.key] ?? ''}
                        placeholder={derived[f.key]}
                        onChange={(e) => setOverride(f.key, e.target.value)}
                        onBlur={() => setEditing(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') setEditing(null);
                        }}
                        className="h-7 flex-1"
                      />
                    ) : (
                      <button
                        type="button"
                        disabled={readOnly || off}
                        onClick={() => setEditing(f.key)}
                        className={cn(
                          'flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          !readOnly && !off && 'hover:bg-accent',
                          off && 'text-muted-foreground line-through',
                        )}
                      >
                        <span className="truncate">{shown || <span className="italic">blank</span>}</span>
                        {/* Marked, because the whole point is knowing which
                            numbers are ours and which the system's. */}
                        {edited && (
                          <span className="shrink-0 rounded bg-brand/15 px-1 text-[10px] text-brand">
                            edited
                          </span>
                        )}
                        {!readOnly && !off && (
                          <Pencil className="ml-auto h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
                        )}
                      </button>
                    )}

                    {edited && !readOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        aria-label={`Reset ${f.label}`}
                        title="Back to the value from the ticket"
                        onClick={() => setOverride(f.key, undefined)}
                      >
                        <Undo2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      disabled={readOnly}
                      aria-label={off ? `Show ${f.label}` : `Hide ${f.label}`}
                      onClick={() => toggleHidden(f.key)}
                    >
                      {off ? (
                        <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
